import { type DialogProps } from '@radix-ui/react-dialog';
import { Command as CommandPrimitive } from 'cmdk';
import { Search } from 'lucide-react';
import * as React from 'react';

import { Dialog, DialogContent } from '@components/ui/dialog';
import { cn } from '@lib/utils';

const Command = (props: React.ComponentProps<typeof CommandPrimitive>) => {
  const { className, ...otherProps } = props;

  return (
    <CommandPrimitive
      className={cn(
        'flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground',
        className,
      )}
      {...otherProps}
    />
  );
};
Command.displayName = CommandPrimitive.displayName;

const CommandDialog = (props: DialogProps) => {
  const { children, ...otherProps } = props;

  return (
    <Dialog {...otherProps}>
      <DialogContent className="overflow-hidden p-0 shadow-lg">
        <Command className="**:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 **:[[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 **:[[cmdk-input]]:h-12 **:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const CommandInput = (props: React.ComponentProps<typeof CommandPrimitive.Input>) => {
  const { className, ...otherProps } = props;

  return (
    <div
      className="flex items-center border-b px-3"
      cmdk-input-wrapper=""
    >
      <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
      <CommandPrimitive.Input
        className={cn(
          'flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...otherProps}
      />
    </div>
  );
};
CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = (props: React.ComponentProps<typeof CommandPrimitive.List>) => {
  const { className, ...otherProps } = props;

  return (
    <CommandPrimitive.List
      className={cn('max-h-[300px] overflow-y-auto overflow-x-hidden', className)}
      {...otherProps}
    />
  );
};
CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = (props: React.ComponentProps<typeof CommandPrimitive.Empty>) => {
  const { className, ...otherProps } = props;

  return (
    <CommandPrimitive.Empty
      className={cn('px-4 py-6 text-center text-sm leading-5 whitespace-normal break-words', className)}
      {...otherProps}
    />
  );
};
CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = (props: React.ComponentProps<typeof CommandPrimitive.Group>) => {
  const { className, ...otherProps } = props;

  return (
    <CommandPrimitive.Group
      className={cn(
        'overflow-hidden p-1 text-foreground **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-muted-foreground',
        className,
      )}
      {...otherProps}
    />
  );
};
CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = (props: React.ComponentProps<typeof CommandPrimitive.Separator>) => {
  const { className, ...otherProps } = props;

  return (
    <CommandPrimitive.Separator
      className={cn('-mx-1 h-px bg-border', className)}
      {...otherProps}
    />
  );
};
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = (props: React.ComponentProps<typeof CommandPrimitive.Item>) => {
  const { className, ...otherProps } = props;

  return (
    <CommandPrimitive.Item
      className={cn(
        "relative flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected='true']:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        className,
      )}
      {...otherProps}
    />
  );
};
CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = (props: React.ComponentProps<'span'>) => {
  const { className, ...otherProps } = props;

  return (
    <span
      className={cn('ml-auto text-xs tracking-widest text-muted-foreground', className)}
      {...otherProps}
    />
  );
};
CommandShortcut.displayName = 'CommandShortcut';

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
